// hooks/useOneTimeStorageClear.ts
import { useEffect, useRef, useState } from 'react'

type ServerUser = { user_uuid: string; isStorageClear?: boolean | null }
type UpdateUserFn = (_args: { user_uuid: string; patch: Record<string, any> }) => void

const DEFAULT_AUTH_KEYS = [
  'google_access_token',
  'slack_access_token',
  'slack_user_access_token',
  'slack_user_id',
  'panda_access_token',
  'panda_refresh_token',
]

export function useOneTimeStorageClear({
  user,
  updateUser,
  keysToKeep = DEFAULT_AUTH_KEYS,
  // optional: wipe only keys with these prefixes instead of full clear
  onlyPrefixes,
}: {
  user: ServerUser | null | undefined
  updateUser: UpdateUserFn
  keysToKeep?: string[]
  onlyPrefixes?: string[] // e.g. ['grid:', 'filters:', 'bootstrap:', 'currentUser']
}) {
  const [ready, setReady] = useState(false)
  const ranRef = useRef(false)

  useEffect(() => {
    // Gate: must have a server user and tokens in storage (i.e., already authenticated)
    if (!user || ranRef.current) return

    const hasTokens =
      localStorage.getItem('google_access_token') &&
      localStorage.getItem('slack_access_token') &&
      localStorage.getItem('panda_access_token')

    if (!hasTokens) return // don't clear until the user is fully authenticated

    ranRef.current = true

    const versionKey = `storage.version::${user.user_uuid}`
    const alreadyMigrated = localStorage.getItem(versionKey) === '1'
    const needsClear = !user.isStorageClear || !alreadyMigrated

    if (!needsClear) {
      setReady(true)
      return
    }

    // preserve auth keys
    const preserved: Record<string, string> = {}
    for (const k of keysToKeep) {
      const v = localStorage.getItem(k)
      if (v != null) preserved[k] = v
    }

    if (onlyPrefixes?.length) {
      // selective wipe: remove only keys that start with allowed prefixes
      const toRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)!
        if (onlyPrefixes.some(p => key.startsWith(p))) toRemove.push(key)
      }
      toRemove.forEach(k => localStorage.removeItem(k))
    } else {
      // full clear, then restore tokens
      localStorage.clear()
    }

    // restore preserved keys
    Object.entries(preserved).forEach(([k, v]) => localStorage.setItem(k, v))

    // mark done (local + server)
    localStorage.setItem(versionKey, '1')
    localStorage.setItem('storageClearedAt', new Date().toISOString())
    updateUser({
      user_uuid: user.user_uuid,
      patch: { isStorageClear: true, storageClearTimestamp: new Date().toISOString() },
    })

    setReady(true)
  }, [user, updateUser, keysToKeep, onlyPrefixes])

  return ready
}
